import React from "react";
import { map } from "lodash";
import styled, { css } from "styled-components";
import { FontWeight, content, transparentize } from "@glossy";
import { Color } from "@glossy/colors";
import Badge from "@ui-kit/chip/badge";
import Icon, { Glyph } from "@ui-kit/icon";
import SafeHTML from "@ui-kit/safe-html";
import Link from "@ui-kit/utils/link";

interface Item<T> {
  label: string;
  id?: T;
  url?: string;
  size?: string;
  disabled?: boolean;
  separated?: boolean;
  subLabel?: string;
  glyph?: Glyph;
  color?: string;
  disk?: boolean;
  style?: object;
  icon?: any;
  svg?: string;
  drawing?: any;
  error?: boolean;
  checked?: boolean;
  count?: number | string;
  onClick?: any;
}

interface IProps {
  items: Item<string | number>[];
  onClick?: (
    item: Item<string | number>,
    event: React.MouseEvent<HTMLDivElement | HTMLAnchorElement, MouseEvent>
  ) => any;
  onClickTitle?: () => any;
  title?: {
    url?: string;
    label: string;
    icon?: any;
  };
  icon?: any;
  pullRight?: boolean;
  size?: Size;
  item?: string | number;
  selectedValue?: string;
  hasChevron?: boolean;
}

type Size = "s" | "m" | "l";

const StyledItem = styled(Link)<{
  size: Size;
  disabled: boolean;
  separated: boolean;
  $isSelected?: boolean;
  $hasChevron?: boolean;
}>`
  display: block;
  cursor: pointer;
  font-size: ${(p) => p.size === "s" && "12px"};
  padding: ${(p) => (p.size === "s" ? "3px" : p.size === "m" ? "8px" : "12px")};
  ${(p) =>
    p.disabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
    `};
  ${(p) =>
    p.separated &&
    css`
      border-top: 1px solid rgba(0, 0, 0, 0.15);
    `};
  transition: background-color 0.3s ease;
  background-color: ${(props) =>
    props.$isSelected && transparentize(0.85, content(props, "primary"))};
  ${(props) =>
    props.$hasChevron &&
    css`
      height: 36px;
      display: flex;
      align-items: center;
    `}
  &:hover,
  &.active {
    text-decoration: none;
    background-color: ${(props) =>
      props.$hasChevron
        ? transparentize(0.85, content(props, "primary"))
        : Color.LightSmoke};
  }
`;

const Title = styled(Link)`
  display: block;
  cursor: pointer;
  padding: 12px 8px;
  border-bottom: 1px solid ${transparentize(0.85, Color.Black)};
  font-weight: bold;
  color: ${Color.Black};
  transition: background-color 0.3s ease;
  &:hover,
  &.active {
    background-color: ${Color.LightSmoke};
  }
`;

const IconTitle = styled(Icon)`
  margin: 0 6px;
`;

const ItemIcon = styled(Icon)`
  display: inline-block;
  vertical-align: top;
  color: ${Color.Black};
`;

const Label = styled.div<{ hasChevron?: boolean }>`
  display: inline-block;
  vertical-align: middle;
  margin: ${(props) => (props.hasChevron ? "0 8px" : "0 5px")};
  font-weight: ${(props) => props.hasChevron && FontWeight.Normal};
  color: ${(props) =>
    props.hasChevron ? content(props, "primary") : Color.Black};
`;

const SubLabel = styled.div`
  font-size: 10px;
  margin-top: 4px;
  font-style: italic;
  color: ${Color.Nobel};
`;

const ItemBadge = styled(Badge)`
  position: absolute;
  top: 4px;
`;

const List = ({
  items,
  item: checkedItem,
  pullRight,
  size,
  selectedValue,
  hasChevron,
  onClick,
  icon,
}: Partial<IProps>) => (
  <>
    {map(items, (item) => {
      const {
        id,
        url,
        disabled,
        separated,
        label,
        icon: itemIcon,
        disk,
      } = item;
      const hasIcon = itemIcon || icon;
      const pullLeft = !pullRight;
      const handleClick = (event) =>
        !disabled && !url && onClick && onClick(item, event);
      const isChecked = item.checked || id === checkedItem;

      return (
        <StyledItem
          target="_blank"
          key={id}
          url={url}
          size={size}
          disabled={disabled}
          separated={separated}
          $isSelected={label === selectedValue}
          $hasChevron={hasChevron}
          onClick={handleClick}
        >
          {pullLeft && hasIcon && (
            <ItemIcon
              glyph={hasIcon}
              disk={disk}
              color={item.color}
              size="ms"
            />
          )}
          {pullLeft && item.drawing && (
            <ItemIcon glyph={item.drawing} size="l" />
          )}
          {pullLeft && hasIcon && <span>&nbsp;&nbsp;</span>}
          <Label hasChevron={hasChevron}>
            <SafeHTML html={label} />
            {item.subLabel && <SubLabel>{item.subLabel}</SubLabel>}
          </Label>
          {pullRight && hasIcon && <span>&nbsp;&nbsp;</span>}
          {pullRight && hasIcon && (
            <ItemIcon
              glyph={hasIcon}
              disk={disk}
              color={item.color}
              size="ms"
            />
          )}
          {!!item.error && <ItemBadge color="danger">!</ItemBadge>}
          {!!item.count && <ItemBadge color={null}>{item.count}</ItemBadge>}
          {isChecked && <ItemIcon color="green" glyph="check" />}
        </StyledItem>
      );
    })}
  </>
);

export const KomonDropdownList = ({
  items,
  onClick,
  onClickTitle,
  icon,
  pullRight,
  title,
  size = "m",
  item,
  selectedValue,
  hasChevron = false,
}: IProps) => (
  <div>
    {!!title && (
      <Title url={title.url || ""} onClick={onClickTitle}>
        {!!title.icon && <IconTitle glyph={title.icon} size="ms" />}
        {<span>{title.label}</span>}
      </Title>
    )}
    <List
      items={items}
      pullRight={pullRight}
      item={item}
      onClick={onClick}
      size={size}
      icon={icon}
      hasChevron={hasChevron}
      selectedValue={selectedValue}
    />
  </div>
);

export default KomonDropdownList;
